import React, { useState } from 'react';
import { Alert, OKRState } from '../types';
import { AlertTriangle, Bell, CheckCircle2 } from 'lucide-react';

const typeLabels: Record<string, string> = {
    'kpi-degrading': 'KPI dégradé', 'objective-delayed': 'Objectif en retard',
    'collaborator-overloaded': 'Surcharge', 'task-blocked': 'Tâche bloquée', 'no-activity': 'Inactivité',
};

const AlertCard: React.FC<{ alert: Alert; onAck: (id: string) => void; state: OKRState }> = ({ alert, onAck, state }) => {
    const cfg = {
        critical: { bg: 'bg-red-50 border-red-200', icon: <AlertTriangle size={17} className="text-red-500 flex-shrink-0 mt-0.5" />, badge: 'bg-red-100 text-red-600', label: 'Critique' },
        warning: { bg: 'bg-orange-50 border-orange-200', icon: <AlertTriangle size={17} className="text-orange-500 flex-shrink-0 mt-0.5" />, badge: 'bg-orange-100 text-orange-600', label: 'Attention' },
        info: { bg: 'bg-blue-50 border-blue-200', icon: <Bell size={17} className="text-blue-400 flex-shrink-0 mt-0.5" />, badge: 'bg-blue-100 text-blue-600', label: 'Info' },
    }[alert.severity];

    const relatedObj = alert.objectiveId ? state.objectives.find((o) => o.id === alert.objectiveId || o._id === alert.objectiveId) : undefined;
    const relatedUser = alert.userId ? state.team.find((m) => m.id === alert.userId) : undefined;

    const ago = (() => {
        const diff = Date.now() - new Date(alert.createdAt).getTime();
        const d = Math.floor(diff / 86400000);
        const h = Math.floor(diff / 3600000);
        return d > 0 ? `Il y a ${d}j` : h > 0 ? `Il y a ${h}h` : 'Récemment';
    })();

    return (
        <div className={`rounded-2xl border p-4 flex items-start gap-3 ${cfg.bg} ${alert.acknowledged ? 'opacity-50' : ''}`}>
            {cfg.icon}
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label}</span>
                    <span className="text-xs text-gray-500 bg-white/80 px-2 py-0.5 rounded-full border border-gray-100">{typeLabels[alert.type] ?? alert.type}</span>
                    {relatedObj && <span className="text-xs text-gray-500">{relatedObj.name}</span>}
                    {relatedUser && <span className="text-xs text-gray-500">{relatedUser.name}</span>}
                </div>
                <p className="text-sm font-bold text-navy-700 mb-0.5">{alert.title}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{alert.message}</p>
                <p className="text-xs text-gray-400 mt-1.5">{ago}</p>
            </div>
            {!alert.acknowledged && (
                <button onClick={() => onAck(alert.id)} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-green-600 bg-white/80 hover:bg-green-50 border border-gray-200 hover:border-green-200 rounded-lg px-2.5 py-1.5 transition-all flex-shrink-0">
                    <CheckCircle2 size={13} /> Traité
                </button>
            )}
        </div>
    );
};

const AlertsPanel: React.FC<{ state: OKRState; onAcknowledge: (id: string) => void }> = ({ state, onAcknowledge }) => {
    const [showDone, setShowDone] = useState(false);
    const active = state.alerts.filter((a) => !a.acknowledged);
    const done = state.alerts.filter((a) => a.acknowledged);
    const critical = active.filter((a) => a.severity === 'critical');
    const warnings = active.filter((a) => a.severity === 'warning');
    const infos = active.filter((a) => a.severity === 'info');

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600">{active.length} alerte{active.length > 1 ? 's' : ''} active{active.length > 1 ? 's' : ''}</span>
                    {critical.length > 0 && <span className="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{critical.length} critique{critical.length > 1 ? 's' : ''}</span>}
                </div>
                {done.length > 0 && <button onClick={() => setShowDone(!showDone)} className="text-xs text-gray-400 hover:text-gray-600">{showDone ? 'Masquer traitées' : `Voir ${done.length} traitée${done.length > 1 ? 's' : ''}`}</button>}
            </div>

            {active.length === 0 && (
                <div className="rounded-2xl border-2 border-dashed border-gray-100 p-16 text-center">
                    <CheckCircle2 size={48} className="mx-auto mb-3 text-green-300" />
                    <p className="text-gray-400 font-medium">Aucune alerte active</p>
                </div>
            )}

            {[{ list: critical, label: `Critiques (${critical.length})`, color: 'text-red-500' }, { list: warnings, label: `Attention (${warnings.length})`, color: 'text-orange-500' }, { list: infos, label: `Informations (${infos.length})`, color: 'text-blue-400' }].map(({ list, label, color }) =>
                list.length > 0 ? (
                    <div key={label}>
                        <p className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${color}`}><AlertTriangle size={12} />{label}</p>
                        <div className="space-y-3">{list.map((a) => <AlertCard key={a.id} alert={a} onAck={onAcknowledge} state={state} />)}</div>
                    </div>
                ) : null
            )}

            {showDone && done.length > 0 && (
                <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Traitées ({done.length})</p>
                    <div className="space-y-3">{done.map((a) => <AlertCard key={a.id} alert={a} onAck={onAcknowledge} state={state} />)}</div>
                </div>
            )}
        </div>
    );
};

export default AlertsPanel;
